/**
 * Note Viewer Context
 *
 * Provides a global read-only note viewer that can be opened from anywhere in the app.
 * This allows modules like Activity Feed to view notes without needing a Notes module on the page.
 *
 * The viewer is rendered as an overlay at the app level.
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';

/** Sanitize HTML to prevent XSS — strips scripts, event handlers, and dangerous tags */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'span', 'div', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'sub', 'sup', 'mark', 'del', 's'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'class', 'style', 'id'],
    ALLOW_DATA_ATTR: false,
  });
}
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from '@/features/auth/hooks/useSession';
import { http } from '@/lib/http';
import '@/modules/_infrastructure/ModuleStyles.css';

// Note object type
interface NoteObject {
  id: string;
  type: 'note';
  title?: string;
  body?: string;
  content?: string;
  authorId?: string;
  authorName?: string;
  createdAt?: string;
  updatedAt?: string;
  noteType?: 'note';
}

interface NoteEditorContextValue {
  openNote: (noteId: string, conversationId?: string) => Promise<void>;
  closeNote: () => void;
  isOpen: boolean;
}

const NoteEditorContext = createContext<NoteEditorContextValue | undefined>(undefined);

export function NoteEditorProvider({ children }: { children: ReactNode }) {
  const { token } = useSession();

  // State for the viewer overlay
  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState<NoteObject | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Open a note by ID (view-only)
  const openNote = useCallback(async (noteId: string, _conversationId?: string) => {
    if (!token) return;

    setIsLoading(true);
    setIsOpen(true);

    try {
      const response = await http.get<NoteObject>(`/notes/${noteId}`, { authToken: token });
      if (response.data) {
        setNote(response.data);
      }
    } catch (err) {
      console.error('[NoteEditorContext] Failed to fetch note:', err);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Close the viewer
  const closeNote = useCallback(() => {
    setIsOpen(false);
    setNote(null);
  }, []);

  // Close on escape and manage body class for mobile
  useEffect(() => {
    if (!isOpen) return;

    // Add body class to hide header/footer on mobile
    document.body.classList.add('modal-open');

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeNote();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, closeNote]);

  const contextValue = useMemo(() => ({
    openNote,
    closeNote,
    isOpen,
  }), [openNote, closeNote, isOpen]);

  // Click-outside-to-close with mousedown tracking (prevents accidental close during text selection)
  const [noteViewerRef, handleNoteOverlayClick, handleNoteOverlayMouseDown] = useModalOverlayClick(closeNote);

  // Get display title
  const displayTitle = note?.title || 'Untitled';

  // Get content - handle both body and content fields
  // Convert literal \n sequences (from imported/ingested data) to <br> for display
  const rawContent = note?.body || note?.content || '';
  const noteContent = rawContent.includes('<') ? rawContent : rawContent.replace(/\\n/g, '<br>');

  // Get portal root
  const portalRoot = typeof document !== 'undefined'
    ? document.getElementById('modal-portal-root')
    : null;

  // Modal content
  const modalContent = isOpen ? (
    <div
      className="edit-modal-overlay fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      onMouseDown={handleNoteOverlayMouseDown}
      onClick={handleNoteOverlayClick}
    >
      <div
        ref={noteViewerRef}
        className="edit-modal-content bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="p-10 text-center text-gray-500">
            Loading note...
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate pr-2" style={{ maxWidth: 'calc(100% - 40px)' }}>
                {displayTitle}
              </h2>
              <button
                onClick={closeNote}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3 overflow-y-auto flex-1 xv-scroll">
              {/* Note content */}
              {noteContent ? (
                <div
                  className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(noteContent) }}
                />
              ) : (
                <div className="text-sm text-gray-400 italic">
                  No content
                </div>
              )}

              {/* Created by / Created on - muted styling with separator */}
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                <div className="flex gap-2 text-xs text-gray-400">
                  <span className="w-20 shrink-0">Created by:</span>
                  <span>{note?.authorName || 'Unknown'}</span>
                </div>
                <div className="flex gap-2 text-xs text-gray-400">
                  <span className="w-20 shrink-0">Created on:</span>
                  <span>
                    {note?.createdAt
                      ? `${new Date(note.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} at ${new Date(note.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                      : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <NoteEditorContext.Provider value={contextValue}>
      {children}

      {/* Note Viewer Overlay - rendered in portal for proper z-index on mobile */}
      {modalContent && portalRoot && createPortal(modalContent, portalRoot)}
    </NoteEditorContext.Provider>
  );
}

export function useNoteEditor() {
  const context = useContext(NoteEditorContext);
  if (!context) {
    throw new Error('useNoteEditor must be used within a NoteEditorProvider');
  }
  return context;
}
